import React, { useEffect, useMemo, useRef, useState } from 'react'
import { scanFinFile } from '../lib/tabLogic'
import { StepIcon, Toggle, PillMeter, NumberPills, InfoBadge, Dropdown, Swatch, UploadTile, StepHeader } from './vehicle-details'

const headerColors = { active: '#9c00b3', inactive: '#8c8f97' }
const stepIcons = {
  step1: '/images/step1icon.png',
  step2: '/images/step2icon.png',
  step3: '/images/step3icon.png',
}

const stepConfig = [
  { id: 1, label: 'FIN scan / Fotos', icon: 'step1', helper: 'FIN scannen oder eingeben' },
  { id: 2, label: 'Weitere Informationen', icon: 'step2', helper: 'Ausstattung & Farbe' },
  { id: 3, label: 'Zustand', icon: 'step3', helper: 'Zustand & Historie' },
]

const VehicleDetailSteps = ({ className = '', defaultOpen = 1 }) => {
  const [open, setOpen] = useState(defaultOpen)
  const [displayedStep, setDisplayedStep] = useState(defaultOpen)
  const [desktopAnimating, setDesktopAnimating] = useState(false)
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false)
  const [fin, setFin] = useState('')
  const [finError, setFinError] = useState('')
  const [finScanning, setFinScanning] = useState(false)
  const [zb1Preview, setZb1Preview] = useState('/images/FIN.png')
  const [photos, setPhotos] = useState({})
  const [targetPhotoKey, setTargetPhotoKey] = useState(null)

  const [innen, setInnen] = useState(null)
  const [aussen, setAussen] = useState(null)
  const [halter, setHalter] = useState(null)
  const [toggleState, setToggleState] = useState({
    klima: true,
    fahrbereit: true,
    warnleuchte: false,
    unfallfrei: true,
    tuvm6: true,
    zweitradsatz: false,
    extraschluessel: true,
    finanzierung: true,
  })
  const [aussenfarbe, setAussenfarbe] = useState('')
  const [sitzfarbe, setSitzfarbe] = useState('')
  const [innenmaterial, setInnenmaterial] = useState('')
  const [sitze, setSitze] = useState('')
  const [step2Errors, setStep2Errors] = useState({})

  const finInputRef = useRef(null)
  const finScanInputRef = useRef(null)
  const zb1InputRef = useRef(null)
  const carPhotoInputRef = useRef(null)
  const animationTimeoutRef = useRef({ out: null, in: null })

  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const mq = window.matchMedia('(min-width: 1024px)')
    const handler = (event) => setIsDesktop(event.matches)
    if (mq.addEventListener) {
      mq.addEventListener('change', handler)
    } else {
      mq.addListener(handler)
    }
    setIsDesktop(mq.matches)
    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handler)
      } else {
        mq.removeListener(handler)
      }
    }
  }, [])

  useEffect(() => {
    if (isDesktop && open === 0) {
      setOpen(1)
      setDisplayedStep(1)
    }
    if (!isDesktop) {
      setDisplayedStep(open)
    }
  }, [isDesktop, open])

  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current.out) clearTimeout(animationTimeoutRef.current.out)
      if (animationTimeoutRef.current.in) clearTimeout(animationTimeoutRef.current.in)
    }
  }, [])

  const photoSlots = useMemo(
    () => [
      { label: 'Vorn', placeholder: '/images/front.png' },
      { label: 'Seite', placeholder: '/images/side.png' },
      { label: 'Hinten', placeholder: '/images/back.png' },
      { label: 'Seite 2', placeholder: '/images/side1.png' },
      { label: 'Innen', placeholder: '/images/inner.png' },
      { label: 'Innen 2', placeholder: '/images/inner2.png' },
    ],
    []
  )

  const colorOptions = ['Schwarz', 'Grau', 'Blau', 'Weiß', 'Silber', 'andere']
  const colorMap = {
    Schwarz: '#000000',
    Grau: '#808080',
    Blau: '#1d4ed8',
    Weiß: '#f8fafc',
    Silber: '#d1d5db',
  }

  const goToStep = (step) => {
    if (step === open) return
    if (isDesktop && step === 0) {
      setOpen(1)
      return
    }
    setOpen(step)
    if (isDesktop) {
      const nextStep = step === 0 ? 1 : step
      if (animationTimeoutRef.current.out) clearTimeout(animationTimeoutRef.current.out)
      if (animationTimeoutRef.current.in) clearTimeout(animationTimeoutRef.current.in)
      setDesktopAnimating(true)
      animationTimeoutRef.current.out = setTimeout(() => {
        setDisplayedStep(nextStep)
        animationTimeoutRef.current.in = setTimeout(() => {
          setDesktopAnimating(false)
        }, 160)
      }, 140)
    } else {
      setDisplayedStep(step)
    }
  }

  const handleToggle = (key) => setToggleState((prev) => ({ ...prev, [key]: !prev[key] }))

  const handleFinFile = async (file) => {
    if (!file) return
    setFinScanning(true)
    setFinError('')
    try {
      const result = await scanFinFile(file)
      if (result?.fin) {
        setFin(result.fin)
      } else {
        setFinError('FIN konnte nicht erkannt werden. Bitte neues Foto versuchen.')
      }
    } catch (err) {
      console.error(err)
      setFinError('FIN konnte nicht erkannt werden. Bitte neues Foto versuchen.')
    } finally {
      setFinScanning(false)
    }
  }

  const handleFinFileChange = async (event, isZb1 = false) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (isZb1) {
      const preview = await fileToDataUrl(file)
      setZb1Preview(preview)
    }
    await handleFinFile(file)
    event.target.value = ''
  }

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0]
    if (!file || !targetPhotoKey) return
    const preview = await fileToDataUrl(file)
    setPhotos((prev) => ({ ...prev, [targetPhotoKey]: preview }))
    setTargetPhotoKey(null)
    event.target.value = ''
  }

  const handleStep2Next = () => {
    const errs = {}
    if (!aussenfarbe) errs.aussenfarbe = true
    if (!sitzfarbe) errs.sitzfarbe = true
    if (!innenmaterial) errs.innenmaterial = true
    if (!sitze) errs.sitze = true
    setStep2Errors(errs)
    if (Object.keys(errs).length === 0) goToStep(3)
  }

  const renderPhotoStrip = (placement = 'mobile') => {
    if (!Object.keys(photos).length) return null
    const baseClass =
      placement === 'desktop'
        ? 'bg-white border border-gray-200 rounded-2xl px-3 py-2 shadow-sm'
        : 'bg-white/80 border-b border-gray-300 px-3 py-2 space-y-1'
    return (
      <div className={`${baseClass} space-y-1`}>
        <p className='text-xs font-semibold text-gray-600'>Deine Fotos</p>
        <div className='flex items-center gap-3 overflow-x-auto pb-1'>
          {photoSlots
            .map((slot) => ({ label: slot.label, url: photos[slot.label] }))
            .filter((item) => item.url)
            .map((item) => (
              <img
                key={item.label}
                src={item.url}
                alt={item.label}
                className='h-14 w-20 object-cover rounded-xl border border-gray-200 flex-shrink-0'
              />
            ))}
        </div>
      </div>
    )
  }

  const renderStepOne = (variant = 'mobile') => {
    const desktop = variant === 'desktop'
    const stackSpacing = desktop ? 'space-y-4' : 'space-y-5'
    const inputClasses = desktop
      ? 'flex-1 min-w-[260px] h-11 rounded-xl border border-gray-300 px-4 text-base bg-white/90 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#0099ff]'
      : 'flex-1 min-w-[0] h-12 rounded-full border border-gray-300 px-4 text-base bg-white/90'
    const buttonClasses = desktop
      ? 'flex-none px-4 h-11 rounded-full bg-[#0099ff] text-white font-semibold shadow hover:bg-[#0085e6] transition disabled:opacity-60'
      : 'flex-none px-5 h-12 rounded-full bg-[#00a7ff] text-white font-semibold shadow disabled:opacity-60'
    const uploadButtonClasses = desktop
      ? 'inline-flex items-center justify-center px-5 h-11 rounded-full bg-[#0099ff] text-white text-sm font-semibold shadow hover:bg-[#0085e6]'
      : 'px-5 py-2 rounded-full bg-[#00a7ff] text-white text-sm font-semibold shadow w-[180px]'

    if (desktop) {
      return (
        <div className={`${stackSpacing} max-w-4xl`}>
          <div className='bg-white border border-gray-200 rounded-2xl shadow-sm p-4 space-y-4'>
            <div className='grid grid-cols-[1.05fr,0.95fr] gap-4'>
              <div className='rounded-xl border border-gray-200 bg-[#f9fafb] p-4 space-y-3'>
                <div className='space-y-1'>
                  <div className='flex items-center gap-2 text-sm text-gray-900'>
                    <span className='font-semibold'>FIN-Nummer</span>
                    <InfoBadge />
                  </div>
                  <p className='text-xs text-gray-600'>Fahrzeug-Identifizierungsnummer</p>
                </div>
                <div className='space-y-2'>
                  <input
                    id='mobile-fin-input'
                    ref={finInputRef}
                    type='text'
                    value={fin}
                    onChange={(e) => setFin(e.target.value)}
                    placeholder='z.B. W0L000051T2132456'
                    className={`${inputClasses} w-full`}
                  />
                  <div className='flex items-start gap-2 text-xs text-gray-600 leading-snug text-justify'>
                    <InfoBadge />
                    <p className='leading-snug'>
                      Die 17-stellige FIN finden Sie in der Zulassungsbescheinigung Teil 1 (Fahrzeugschein) im Feld E, im Fahrzeugbrief
                      (Zulassungsbescheinigung Teil 2) unter Nummer 4 und direkt am Fahrzeug, z. B. im Motorraum, am Türrahmen, unter der
                      Windschutzscheibe (Fahrerseite) oder im Kofferraum.
                    </p>
                  </div>
                  <div className='flex justify-end'>
                    <button
                      type='button'
                      disabled={finScanning}
                      onClick={() => finScanInputRef.current?.click()}
                      className='inline-flex items-center justify-center px-5 h-11 rounded-full bg-[#0099ff] text-white font-semibold shadow hover:bg-[#0085e6] transition disabled:opacity-60'
                    >
                      {finScanning ? 'Scanne...' : 'Scan'}
                    </button>
                  </div>
                  <input
                    ref={finScanInputRef}
                    type='file'
                    accept='image/*'
                    capture='environment'
                    className='hidden'
                    onChange={(e) => handleFinFileChange(e, false)}
                  />
                </div>
                {finError ? <p className='text-sm text-red-600'>{finError}</p> : null}
              </div>

              <div className='rounded-xl border border-gray-200 bg-[#f9fafb] p-4 space-y-3'>
                <div className='space-y-1'>
                  <p className='text-sm font-semibold text-gray-900'>Zulassung (ZB I)</p>
                  <p className='text-xs text-gray-600 leading-relaxed'>Foto hochladen für eine präzise Zuordnung.</p>
                </div>
                <div className='relative h-[140px] rounded-xl border border-dashed border-[#cfd7e4] bg-[#f8fbff] overflow-hidden'>
                  {zb1Preview ? (
                    <img src={zb1Preview} alt='Zulassung (ZB 1)' className='absolute inset-0 h-full w-full object-cover opacity-50' />
                  ) : null}
                  <div className='absolute inset-0 flex items-center justify-center px-4 text-sm text-gray-600 text-center'>
                    Foto hier ablegen oder «ZB I hochladen».
                  </div>
                </div>
                <div className='flex justify-end'>
                  <button type='button' onClick={() => zb1InputRef.current?.click()} className={uploadButtonClasses}>
                    ZB I hochladen
                  </button>
                </div>
                <input
                  ref={zb1InputRef}
                  type='file'
                  accept='image/*'
                  capture='environment'
                  className='hidden'
                  onChange={(e) => handleFinFileChange(e, true)}
                />
              </div>
            </div>
          </div>

          <div className='space-y-3'>
            <div className='flex items-center justify-between gap-3'>
              <p className='text-lg font-semibold text-gray-900'>Innen und Außen Fotos</p>
              <span className='text-xs text-gray-600'>Mindestens 6 Perspektiven helfen uns bei der Einschätzung.</span>
            </div>
            <div className='grid gap-2.5 grid-cols-3 xl:grid-cols-6'>
              {photoSlots.map((slot) => (
                <UploadTile
                  key={slot.label}
                  label={slot.label}
                  placeholder={slot.placeholder}
                  preview={photos[slot.label]}
                  variant='desktop'
                  onClick={() => {
                    setTargetPhotoKey(slot.label)
                    carPhotoInputRef.current?.click()
                  }}
                />
              ))}
            </div>
            <input
              ref={carPhotoInputRef}
              type='file'
              accept='image/*'
              capture='environment'
              className='hidden'
              onChange={handlePhotoUpload}
            />
          </div>

          <div className='flex justify-end'>
            <button
              type='button'
              onClick={() => goToStep(2)}
              className='min-w-[180px] px-6 h-12 rounded-full bg-[#00a7ff] text-white font-semibold shadow'
            >
              Weiter
            </button>
          </div>
        </div>
      )
    }

    return (
      <div className={`${stackSpacing}`}>
        <div className='space-y-2'>
          <div className={`flex items-center gap-2 ${desktop ? 'text-base' : 'text-sm'} text-gray-900`}>
            <span>FIN-Nummer:</span>
            <InfoBadge />
          </div>
          <div className={`flex items-center gap-3 flex-wrap ${desktop ? 'justify-start' : ''}`}>
            <input
              id='mobile-fin-input'
              ref={finInputRef}
              type='text'
              value={fin}
              onChange={(e) => setFin(e.target.value)}
              placeholder='z.B. W0L000051T2132456'
              className={inputClasses}
            />
            <button
              type='button'
              disabled={finScanning}
              onClick={() => finScanInputRef.current?.click()}
              className={buttonClasses}
            >
              {finScanning ? 'Scanne...' : 'Scan'}
            </button>
            <input
              ref={finScanInputRef}
              type='file'
              accept='image/*'
              capture='environment'
              className='hidden'
              onChange={(e) => handleFinFileChange(e, false)}
            />
          </div>
          {finError ? <p className='text-sm text-red-600'>{finError}</p> : null}
        </div>

        <div
          className={`relative rounded-2xl border-2 border-dashed ${
            desktop ? 'border-[#cfd7e4] bg-[#f8fbff] min-h-[190px]' : 'border-gray-300 bg-white'
          } overflow-hidden`}
        >
          {zb1Preview ? (
            <img
              src={zb1Preview}
              alt='Zulassung (ZB 1)'
              className={`absolute inset-0 h-full w-full object-cover ${desktop ? 'opacity-30' : ''}`}
            />
          ) : null}
          <div className='relative flex items-center justify-center py-10 px-4'>
            <button type='button' onClick={() => zb1InputRef.current?.click()} className={uploadButtonClasses}>
              Zulassung (ZB I)
            </button>
          </div>
          <input
            ref={zb1InputRef}
            type='file'
            accept='image/*'
            capture='environment'
            className='hidden'
            onChange={(e) => handleFinFileChange(e, true)}
          />
        </div>

        <div className='space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <p className={`${desktop ? 'text-lg' : 'text-sm'} font-semibold text-gray-900`}>Innen und Außen Fotos</p>
            {desktop ? <span className='text-xs text-gray-600'>Mindestens 6 Perspektiven helfen uns bei der Einschätzung.</span> : null}
          </div>
          <div className={`grid ${desktop ? 'gap-2.5 grid-cols-3 xl:grid-cols-6' : 'gap-3 grid-cols-3'}`}>
            {photoSlots.map((slot) => (
              <UploadTile
                key={slot.label}
                label={slot.label}
                placeholder={slot.placeholder}
                preview={photos[slot.label]}
                variant={desktop ? 'desktop' : 'mobile'}
                onClick={() => {
                  setTargetPhotoKey(slot.label)
                  carPhotoInputRef.current?.click()
                }}
              />
            ))}
          </div>
          <input
            ref={carPhotoInputRef}
            type='file'
            accept='image/*'
            capture='environment'
            className='hidden'
            onChange={handlePhotoUpload}
          />
        </div>

        <div className={`${desktop ? 'flex justify-end' : ''}`}>
          <button
            type='button'
            onClick={() => goToStep(2)}
            className={`${desktop ? 'min-w-[180px] px-6' : 'w-full'} h-12 rounded-full bg-[#00a7ff] text-white font-semibold shadow`}
          >
            Weiter
          </button>
        </div>
      </div>
    )
  }

  const renderStepTwo = (variant = 'mobile') => {
    const desktop = variant === 'desktop'
    return (
      <div className={`space-y-5 ${desktop ? 'max-w-4xl' : ''}`}>
        <div className='space-y-3'>
          <p className='text-sm font-semibold text-gray-900'>Außenfarbe</p>
          <div
            className={`grid grid-cols-6 gap-3 rounded-xl p-2 ${step2Errors.aussenfarbe ? 'border-2 border-red-400' : ''}`}
          >
            {colorOptions.map((c) => (
              <Swatch
                key={c}
                label={c}
                color={colorMap[c]}
                value={aussenfarbe}
                onSelect={(val) => {
                  setAussenfarbe(val)
                  setStep2Errors((prev) => ({ ...prev, aussenfarbe: false }))
                }}
              />
            ))}
          </div>
        </div>

        <div className='space-y-3'>
          <p className='text-sm font-semibold text-gray-900'>Sitzfarbe</p>
          <div
            className={`grid grid-cols-6 gap-3 rounded-xl p-2 ${step2Errors.sitzfarbe ? 'border-2 border-red-400' : ''}`}
          >
            {colorOptions.map((c) => (
              <Swatch
                key={c}
                label={c}
                color={colorMap[c]}
                value={sitzfarbe}
                onSelect={(val) => {
                  setSitzfarbe(val)
                  setStep2Errors((prev) => ({ ...prev, sitzfarbe: false }))
                }}
              />
            ))}
          </div>
        </div>

        <div className='flex items-center gap-3 justify-between'>
          <span className='text-sm text-gray-800 whitespace-nowrap'>Material Innenausstattung</span>
          <div className='flex-1 min-w-[180px] max-w-[220px] ml-auto'>
            <Dropdown>
                  <select
                    value={innenmaterial}
                    onChange={(e) => setInnenmaterial(e.target.value)}
                    className={`h-10 rounded-full border px-4 text-sm bg-white/90 min-w-[150px] pr-9 appearance-none w-full ${
                      step2Errors.innenmaterial ? 'border-red-400' : 'border-gray-300'
                    }`}
                    style={{ boxShadow: 'none' }}
                  >
                <option value=''>auswählen</option>
                <option value='Stoff'>Stoff</option>
                <option value='Leder'>Leder</option>
                <option value='Kunstleder'>Kunstleder</option>
                <option value='Mikrofaser'>Mikrofaser</option>
              </select>
            </Dropdown>
          </div>
        </div>

        <div className='flex items-center gap-3 justify-between'>
          <span className='text-sm text-gray-800 whitespace-nowrap'>Anzahl Sitzplätze</span>
          <div className='flex-1 min-w-[180px] max-w-[220px] ml-auto'>
            <Dropdown>
                  <select
                    value={sitze}
                    onChange={(e) => setSitze(e.target.value)}
                    className={`h-10 rounded-full border px-4 text-sm bg-white/90 min-w-[150px] pr-9 appearance-none w-full ${
                      step2Errors.sitze ? 'border-red-400' : 'border-gray-300'
                    }`}
                    style={{ boxShadow: 'none' }}
                  >
                <option value=''>auswählen</option>
                <option value='2'>2</option>
                <option value='4'>4</option>
                <option value='5'>5</option>
                <option value='7'>7</option>
              </select>
            </Dropdown>
          </div>
        </div>

        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm text-gray-800'>Klimaanlage/Automatik</span>
          <Toggle checked={toggleState.klima} onChange={() => handleToggle('klima')} />
        </div>

        <div className={`${desktop ? 'flex justify-end' : ''}`}>
          <button
            type='button'
            onClick={handleStep2Next}
            className={`${desktop ? 'min-w-[180px] px-6' : 'w-full'} h-12 rounded-full bg-[#00a7ff] text-white font-semibold shadow`}
          >
            Weiter
          </button>
        </div>
      </div>
    )
  }

  const renderStepThree = (variant = 'mobile') => {
    const desktop = variant === 'desktop'
    return (
      <div className={`space-y-3.5 ${desktop ? 'max-w-4xl' : ''}`}>
        {[
          { key: 'tuvm6', label: 'TÜV mehr als 6 Monate' },
          { key: 'fahrbereit', label: 'Ist das Fahrzeug fahrbereit?' },
          { key: 'warnleuchte', label: 'Gibt es dauerhaft aktive Warnleuchten?' },
          { key: 'unfallfrei', label: 'Unfallfrei' },
        ].map((item) => (
          <div key={item.key} className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-900'>{item.label}</span>
              <InfoBadge />
            </div>
            <Toggle checked={toggleState[item.key]} onChange={() => handleToggle(item.key)} />
          </div>
        ))}

        <div className='space-y-1'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-900'>Zustand innen</span>
              <InfoBadge />
            </div>
            <PillMeter value={innen} onChange={setInnen} />
          </div>
          <p className='text-xs text-gray-600 ml-7'>1 = gut | 5 = schlecht</p>
        </div>

        <div className='space-y-1'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-900'>Zustand außen</span>
              <InfoBadge />
            </div>
            <PillMeter value={aussen} onChange={setAussen} />
          </div>
          <p className='text-xs text-gray-600 ml-7'>1 = gut | 5 = schlecht</p>
        </div>

        <div className='space-y-1'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <span className='text-sm text-gray-900'>Anzahl der Fahrzeughalter</span>
              <InfoBadge />
            </div>
            <NumberPills value={halter} onChange={setHalter} options={[1, 2, 3, 4, 5]} />
          </div>
        </div>

        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm text-gray-900'>Serviceheft</span>
          <div className='flex-1 min-w-[230px] max-w-[270px]'>
            <Dropdown>
                  <select className='h-10 rounded-full border px-4 text-sm bg-white/90 min-w-[150px] pr-9 appearance-none w-full' style={{ boxShadow: 'none' }}>
                <option>auswählen</option>
                <option>vollständig</option>
                <option>teilweise</option>
                <option>keines</option>
              </select>
            </Dropdown>
          </div>
        </div>

        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm text-gray-900'>Zweiter Radsatz vorhanden</span>
          <Toggle checked={toggleState.zweitradsatz} onChange={() => handleToggle('zweitradsatz')} />
        </div>

        <div className='flex items-center justify-between gap-3'>
          <span className='text-sm text-gray-900'>Mehr als ein Schlüssel</span>
          <Toggle checked={toggleState.extraschluessel} onChange={() => handleToggle('extraschluessel')} />
        </div>

        <div className='flex items-center justify-between gap-3'>
          <div className='flex items-center gap-2'>
            <span className='text-sm text-gray-900'>Fahrzeug noch in Finanzierung?</span>
            <InfoBadge />
          </div>
          <Toggle checked={toggleState.finanzierung} onChange={() => handleToggle('finanzierung')} />
        </div>

        {toggleState.finanzierung ? (
          <div className='flex items-center gap-3 flex-nowrap'>
            <span className='text-sm text-gray-900 whitespace-nowrap flex-shrink-0'>Restbetrag der Finanzierung</span>
            <div className='flex-1 min-w-[160px] relative'>
              <div className='flex items-center h-12 border border-gray-400 rounded-full bg-white pl-3 pr-10 overflow-hidden'>
                <input
                  type='number'
                  className='flex-1 text-sm focus:outline-none bg-transparent pr-6'
                  placeholder='0'
                  min='0'
                />
                <span className='absolute right-4 text-sm text-gray-700'>€</span>
              </div>
            </div>
          </div>
        ) : null}

        <div className={`${desktop ? 'flex justify-end pt-1' : ''}`}>
          <button
            className={`${desktop ? 'min-w-[180px] px-6' : 'w-full'} h-12 rounded-full bg-[#00a7ff] text-white font-semibold shadow mt-2`}
          >
            Fertig
          </button>
        </div>
      </div>
    )
  }

  const renderStepContent = (step, variant) => {
    if (step === 2) return renderStepTwo(variant)
    if (step === 3) return renderStepThree(variant)
    return renderStepOne(variant)
  }

  const renderDesktopLayout = () => {
    const activeStepLabel = stepConfig.find((item) => item.id === (open || 1))
    return (
      <div className={`w-full ${className}`}>
        <div className='grid lg:grid-cols-[280px,1fr] gap-6 items-start'>
          <aside className='space-y-3'>
            {stepConfig.map((step) => {
              const isActive = step.id === (open || 1)
              return (
                <button
                  key={step.id}
                  type='button'
                  onClick={() => goToStep(step.id)}
                  className={`w-full flex items-center gap-4 rounded-2xl px-4 py-3 border transition-all ${
                    isActive
                      ? 'bg-white border-[#7c3aed] shadow-md ring-2 ring-[#7c3aed]/15'
                      : 'bg-[#e7e9ef] border-transparent hover:border-gray-300'
                  }`}
                >
                    <div className={`h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isActive ? 'bg-[#7c3aed]' : 'bg-white shadow-sm border border-[#d0d4da]'}`}>
                      <StepIcon active={isActive} type={step.icon} />
                    </div>
                  <div className='flex-1 text-left'>
                    <div className='font-semibold text-gray-900 leading-tight'>{step.label}</div>
                    <div className='text-xs text-gray-600'>{step.helper}</div>
                  </div>
                  <div className={`text-sm font-semibold ${isActive ? 'text-[#7c3aed]' : 'text-gray-500'}`}>
                    {step.id}/{stepConfig.length}
                  </div>
                </button>
              )
            })}
          </aside>

          <div className='space-y-4'>
            {renderPhotoStrip('desktop')}
            <section className='bg-white border border-gray-200 rounded-3xl shadow-sm p-5 space-y-5'>
              <div className='flex items-center justify-between gap-4'>
                <div className='inline-flex items-center gap-2 rounded-full bg-[#f0f4ff] text-[#0b5bff] px-3 py-1 text-sm font-semibold'>
                  <span>{activeStepLabel?.label}</span>
                </div>
                <span className='inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#ecf3f8] text-gray-700 text-sm font-semibold'>
                  Schritt {open || 1}/{stepConfig.length}
                </span>
              </div>
              <div
                className={`transition-all duration-300 ease-out ${
                  desktopAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
                }`}
              >
                {renderStepContent(displayedStep || 1, 'desktop')}
              </div>
            </section>
          </div>
        </div>
      </div>
    )
  }

  const renderMobileLayout = () => (
    <div
      className={`w-full max-w-none sm:max-w-xl mx-auto px-0 bg-[#dfe1e6] rounded-2xl border border-gray-300 overflow-hidden shadow-sm ${className}`}
    >
      {renderPhotoStrip('mobile')}

      <div className='border-b border-gray-400 rounded-t-2xl overflow-hidden'>
        <StepHeader
          iconSrc={stepIcons.step1}
          colors={headerColors}
          label='FIN / Fotos'
          step='1'
          total='3'
          active={open === 1}
          onClick={() => goToStep(open === 1 ? 0 : 1)}
          position='first'
        />
        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            open === 1 ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='px-4 pb-5 pt-4 space-y-4' style={{ paddingLeft: '0.4rem', paddingRight: '0.4rem' }}>
            {renderStepOne('mobile')}
          </div>
        </div>
      </div>

      <div className='border-b border-gray-400 overflow-hidden'>
        <StepHeader
          iconSrc={stepIcons.step2}
          colors={headerColors}
          label='Ausstattung'
          step='2'
          total='3'
          active={open === 2}
          onClick={() => goToStep(open === 2 ? 0 : 2)}
          position='middle'
        />
        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            open === 2 ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='px-4 pb-5 pt-4 space-y-4' style={{ paddingLeft: '0.4rem', paddingRight: '0.4rem' }}>
            {renderStepTwo('mobile')}
          </div>
        </div>
      </div>

      <div className={`${open === 3 ? '' : 'rounded-b-2xl'} overflow-hidden`}>
        <StepHeader
          iconSrc={stepIcons.step3}
          colors={headerColors}
          label='Zustand'
          step='3'
          total='3'
          active={open === 3}
          onClick={() => goToStep(open === 3 ? 0 : 3)}
          position='last'
        />
        <div
          className={`transition-all duration-500 ease-in-out overflow-hidden ${
            open === 3 ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'
          }`}
        >
          <div className='px-4 pb-6 pt-4 space-y-3.5' style={{ paddingLeft: '0.4rem', paddingRight: '0.4rem' }}>
            {renderStepThree('mobile')}
          </div>
        </div>
      </div>
    </div>
  )

  return isDesktop ? renderDesktopLayout() : renderMobileLayout()
}

export default VehicleDetailSteps
